function normalizeOptionalString(value) {
  if (value === undefined || value === null) {
    return "";
  }

  const normalizedValue = String(value).trim();
  return normalizedValue.length > 0 ? normalizedValue : "";
}

function humanizeToken(value) {
  const normalizedValue = normalizeOptionalString(value);

  if (!normalizedValue) {
    return "Unknown";
  }

  return normalizedValue
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./u, (character) => character.toUpperCase());
}

function formatFallbackAnswer(value) {
  if (value === undefined || value === null || value === "") {
    return "N/A";
  }

  if (value === true) {
    return "Yes";
  }

  if (value === false) {
    return "No";
  }

  if (Array.isArray(value)) {
    const values = value.map((entry) => normalizeOptionalString(entry)).filter(Boolean);
    return values.length > 0 ? values.join(", ") : "N/A";
  }

  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
}

const WEEKLY_STANDARD_OPTIONS = [
  { value: 0, label: "No worse than usual" },
  { value: 1, label: "A little worse than usual" },
  { value: 2, label: "Clearly worse than usual" },
  { value: 3, label: "Much worse than usual" },
];

const WEEKLY_PHLEGM_OPTIONS = [
  { value: 0, label: "No change or better" },
  { value: 1, label: "A small change" },
  { value: 2, label: "A clear increase or change" },
  { value: 3, label: "A major change" },
  { value: "NA", label: "Not usually present" },
];

const WEEKLY_RELIEVER_OPTIONS = [
  { value: 0, label: "None" },
  { value: 1, label: "1 day" },
  { value: 2, label: "2 to 3 days" },
  { value: 3, label: "4 to 7 days" },
  { value: "NA", label: "Not applicable" },
];

const MONTHLY_QUESTION_DEFINITIONS = [
  {
    id: "RH_MO_WORSENING_EPISODE",
    title: "Worsening episode",
    fieldType: "radio",
    options: [
      { value: "NO", label: "No" },
      { value: "YES_RECOVERED", label: "Yes, and I have recovered" },
      { value: "YES_ONGOING", label: "Yes, and it is still ongoing" },
      { value: "UNSURE", label: "Unsure" },
    ],
  },
  {
    id: "RH_MO_EPISODE_TIMING",
    title: "Episode timing",
    fieldType: "episode-timing",
    timingFields: {
      onsetDateLabel: "Approximate start date",
      durationDaysLabel: "Duration in days",
      ongoingLabel: "Is it still ongoing?",
      unknownLabel: "I can't remember these details",
      ongoingOptions: [
        { value: true, label: "Yes" },
        { value: false, label: "No" },
      ],
    },
  },
  {
    id: "RH_MO_EPISODE_SYMPTOMS",
    title: "Symptoms",
    fieldType: "checkbox-group",
    options: [
      { value: "breathlessness", label: "Breathlessness" },
      { value: "cough", label: "Cough" },
      { value: "morePhlegmOrMucus", label: "More phlegm or mucus" },
      {
        value: "phlegmOrMucusColourChange",
        label: "Change in phlegm or mucus colour",
      },
      { value: "wheezing", label: "Wheezing" },
      { value: "chestTightness", label: "Chest tightness" },
      { value: "reducedUsualActivities", label: "Reduced usual activities" },
      { value: "nightTimeSymptoms", label: "Night time symptoms" },
      { value: "other", label: "Other" },
      { value: "unsure", label: "Unsure" },
    ],
    freeTextFieldId: "RH_MO_EPISODE_SYMPTOMS_OTHER",
    freeTextTriggerValue: "other",
  },
  {
    id: "RH_MO_TREATMENT_INCREASE",
    title: "Treatment or oxygen use",
    fieldType: "radio",
    options: [
      { value: "NO", label: "No" },
      { value: "A_LITTLE_MORE", label: "A little more" },
      { value: "SUBSTANTIALLY_MORE", label: "Substantially more" },
      { value: "NOT_APPLICABLE", label: "Not applicable" },
      { value: "UNSURE", label: "Unsure" },
    ],
  },
  {
    id: "RH_MO_HEALTHCARE_ACTION",
    title: "Healthcare action",
    fieldType: "checkbox-group",
    options: [
      { value: "usualPlan", label: "Used the usual self-management plan" },
      { value: "contactedStudyTeam", label: "Contacted the study or clinic team" },
      {
        value: "contactedCommunityClinician",
        label: "Contacted a community clinician or GP",
      },
      {
        value: "contacted111",
        label: "Contacted an urgent advice or out-of-hours service",
      },
      { value: "receivedAntibiotics", label: "Received antibiotics" },
      {
        value: "receivedOralCorticosteroids",
        label: "Received oral corticosteroids",
      },
      {
        value: "attendedUrgentCare",
        label: "Attended urgent care or the emergency department",
      },
      { value: "admittedToHospital", label: "Was admitted to hospital" },
      { value: "none", label: "None of these" },
      { value: "unsure", label: "Unsure" },
    ],
  },
  {
    id: "RH_MO_RECOVERY_STATUS",
    title: "Recovery status",
    fieldType: "radio",
    options: [
      {
        value: "COMPLETELY_BACK_TO_USUAL",
        label: "Completely back to usual",
      },
      { value: "PARTLY_BACK_TO_USUAL", label: "Partly back to usual" },
      { value: "NOT_BACK_TO_USUAL", label: "Not back to usual" },
      {
        value: "STILL_GETTING_WORSE",
        label: "Symptoms are still getting worse",
      },
      { value: "UNSURE", label: "Unsure" },
    ],
  },
];

const QUESTIONNAIRE_QUESTION_DEFINITIONS = {
  cat: [
    { id: "catCough", title: "Cough", fieldType: "range" },
    { id: "catPhlegm", title: "Phlegm", fieldType: "range" },
    { id: "catChestTightness", title: "Chest tightness", fieldType: "range" },
    { id: "catBreathlessness", title: "Breathlessness", fieldType: "range" },
    { id: "catActivities", title: "Activities", fieldType: "range" },
    { id: "catConfidence", title: "Confidence", fieldType: "range" },
    { id: "catSleep", title: "Sleep", fieldType: "range" },
    { id: "catEnergy", title: "Energy", fieldType: "range" },
  ],
  caat: [
    { id: "catCough", title: "Cough", fieldType: "range" },
    { id: "catPhlegm", title: "Phlegm", fieldType: "range" },
    { id: "catChestTightness", title: "Chest tightness", fieldType: "range" },
    { id: "catBreathlessness", title: "Breathlessness", fieldType: "range" },
    { id: "catActivities", title: "Activities", fieldType: "range" },
    { id: "catConfidence", title: "Confidence", fieldType: "range" },
    { id: "catSleep", title: "Sleep", fieldType: "range" },
    { id: "catEnergy", title: "Energy", fieldType: "range" },
  ],
  "r-scale": [
    { id: "cough", title: "Cough", fieldType: "range" },
    { id: "shortnessOfBreath", title: "Shortness of breath", fieldType: "range" },
    { id: "fatigue", title: "Fatigue", fieldType: "range" },
    { id: "depressedMood", title: "Depressed mood", fieldType: "range" },
    { id: "wellBeing", title: "Overall sense of wellbeing", fieldType: "range" },
  ],
  act: [
    {
      id: "actWorkSchoolHome",
      title:
        "During the last 4 weeks, how much of the time has your asthma kept you from getting as much done at work, school or home?",
      fieldType: "range",
    },
    {
      id: "actShortnessOfBreath",
      title:
        "During the last 4 weeks, how often have you had shortness of breath?",
      fieldType: "range",
    },
    {
      id: "actNightSymptoms",
      title:
        "During the last 4 weeks, how often have your asthma symptoms woken you up at night or earlier than usual in the morning?",
      fieldType: "range",
    },
    {
      id: "actRescueInhalerUse",
      title:
        "During the last 4 weeks, how often have you used your rescue inhaler or nebuliser medication?",
      fieldType: "range",
    },
    {
      id: "actAsthmaControl",
      title: "How would you rate your asthma control during the last 4 weeks?",
      fieldType: "range",
    },
  ],
  "copd-ps": [
    {
      id: "COPD_PS_SHORTNESS_OF_BREATH",
      title: "During the past 4 weeks, how much of the time did you feel short of breath?",
      fieldType: "radio",
      options: [
        { value: "NONE_OF_THE_TIME", label: "None of the time" },
        { value: "A_LITTLE_OF_THE_TIME", label: "A little of the time" },
        { value: "SOME_OF_THE_TIME", label: "Some of the time" },
        { value: "MOST_OF_THE_TIME", label: "Most of the time" },
        { value: "ALL_OF_THE_TIME", label: "All of the time" },
      ],
    },
    {
      id: "COPD_PS_PHLEGM",
      title: "Do you ever cough up any \"stuff,\" such as mucus or phlegm?",
      fieldType: "radio",
      options: [
        { value: "NO_NEVER", label: "No, never" },
        {
          value: "ONLY_WITH_OCCASIONAL_COLDS_OR_CHEST_INFECTIONS",
          label: "Only with occasional colds or chest infections",
        },
        { value: "A_FEW_DAYS_A_MONTH", label: "Yes, a few days a month" },
        { value: "MOST_DAYS_A_WEEK", label: "Yes, most days a week" },
        { value: "EVERY_DAY", label: "Yes, every day" },
      ],
    },
    {
      id: "COPD_PS_ACTIVITY_LIMITATION",
      title: "I do less than I used to because of my breathing problems.",
      fieldType: "radio",
      options: [
        { value: "STRONGLY_DISAGREE", label: "Strongly disagree" },
        { value: "DISAGREE", label: "Disagree" },
        { value: "UNSURE", label: "Unsure" },
        { value: "AGREE", label: "Agree" },
        { value: "STRONGLY_AGREE", label: "Strongly agree" },
      ],
    },
    {
      id: "COPD_PS_SMOKING_HISTORY",
      title: "Have you smoked at least 100 cigarettes in your ENTIRE LIFE?",
      fieldType: "radio",
      options: [
        { value: "NO", label: "No" },
        { value: "YES", label: "Yes" },
        { value: "DONT_KNOW", label: "Don’t know" },
      ],
    },
    {
      id: "COPD_PS_AGE",
      title: "How old are you?",
      fieldType: "radio",
      options: [
        { value: "AGE_35_TO_49", label: "Age 35 to 49" },
        { value: "AGE_50_TO_59", label: "Age 50 to 59" },
        { value: "AGE_60_TO_69", label: "Age 60 to 69" },
        { value: "AGE_70_PLUS", label: "Age 70+" },
      ],
    },
  ],
  "weekly-check-in": [
    {
      id: "RH_WK_BREATHLESSNESS_CHANGE",
      title: "Breathlessness",
      fieldType: "radio",
      options: WEEKLY_STANDARD_OPTIONS,
    },
    {
      id: "RH_WK_COUGH_CHANGE",
      title: "Cough",
      fieldType: "radio",
      options: WEEKLY_STANDARD_OPTIONS,
    },
    {
      id: "RH_WK_PHLEGM_CHANGE",
      title: "Phlegm or mucus",
      fieldType: "radio",
      options: WEEKLY_PHLEGM_OPTIONS,
    },
    {
      id: "RH_WK_RELIEVER_INCREASE",
      title: "Inhaler use",
      fieldType: "radio",
      options: WEEKLY_RELIEVER_OPTIONS,
    },
    {
      id: "RH_WK_ACTIVITY_CHANGE",
      title: "Activities",
      fieldType: "radio",
      options: WEEKLY_STANDARD_OPTIONS,
    },
  ],
  "monthly-check-in": MONTHLY_QUESTION_DEFINITIONS,
  sgrq: [
    {
      id: "SGRQ_CURRENT_HEALTH",
      title: "Please tick one box to show how you describe your current health:",
      fieldType: "radio",
    },
    { id: "SGRQ_P1_Q1", title: "Over the past 3 months, I have coughed:", fieldType: "radio" },
    {
      id: "SGRQ_P1_Q2",
      title: "Over the past 3 months, I have brought up phlegm (sputum):",
      fieldType: "radio",
    },
    {
      id: "SGRQ_P1_Q3",
      title: "Over the past 3 months, I have had shortness of breath:",
      fieldType: "radio",
    },
    {
      id: "SGRQ_P1_Q4",
      title: "Over the past 3 months, I have had attacks of wheezing:",
      fieldType: "radio",
    },
    {
      id: "SGRQ_P1_Q5",
      title:
        "During the past 3 months how many severe or very unpleasant attacks of chest trouble have you had?",
      fieldType: "radio",
    },
    {
      id: "SGRQ_P1_Q6",
      title:
        "How long did the worst attack of chest trouble last? (Go to question 7 if you had no severe attacks)",
      fieldType: "radio",
    },
    {
      id: "SGRQ_P1_Q7",
      title:
        "Over the past 3 months, in an average week, how many good days (with little chest trouble) have you had?",
      fieldType: "radio",
    },
    {
      id: "SGRQ_P1_Q8",
      title: "If you have a wheeze, is it worse in the morning?",
      fieldType: "radio",
    },
    {
      id: "SGRQ_P2_S1_CONDITION",
      title: "How would you describe your chest condition?",
      fieldType: "radio",
    },
    {
      id: "SGRQ_P2_S1_WORK",
      title: "If you have ever had paid employment.",
      fieldType: "radio",
    },
    {
      id: "SGRQ_P2_S2_MATRIX",
      title: "Questions about what activities usually make you feel breathless these days.",
      fieldType: "boolean-matrix",
    },
    {
      id: "SGRQ_P2_S3_MATRIX",
      title: "Some more questions about your cough and breathlessness these days.",
      fieldType: "boolean-matrix",
    },
    {
      id: "SGRQ_P2_S4_MATRIX",
      title: "Questions about other effects that your chest trouble may have on you these days.",
      fieldType: "boolean-matrix",
    },
    {
      id: "SGRQ_P2_S5_MATRIX",
      title:
        "Questions about your medication (if you are receiving no medication go straight to Section 6).",
      fieldType: "boolean-matrix",
    },
    {
      id: "SGRQ_P2_S6_MATRIX",
      title: "These are questions about how your activities might be affected by your breathing.",
      fieldType: "boolean-matrix",
    },
    {
      id: "SGRQ_P2_S7_MATRIX",
      title: "We would like to know how your chest usually affects your daily life.",
      fieldType: "boolean-matrix",
    },
    {
      id: "SGRQ_P2_S7_OTHER",
      title: "Other important activities affected by chest trouble",
      fieldType: "textarea",
    },
    {
      id: "SGRQ_P2_S7_OVERALL",
      title:
        "Now tick the box which you think best describes how your chest affects you:",
      fieldType: "radio",
    },
  ],
};

const QUESTIONNAIRE_QUESTION_MAPS = Object.fromEntries(
  Object.entries(QUESTIONNAIRE_QUESTION_DEFINITIONS).map(
    ([questionnaireType, questions]) => [
      questionnaireType,
      new Map(
        questions.map((question) => [question.id.toLowerCase(), question]),
      ),
    ],
  ),
);

const QUESTIONNAIRE_AUXILIARY_FIELD_IDS = new Set(
  Object.values(QUESTIONNAIRE_QUESTION_DEFINITIONS)
    .flatMap((questions) => questions)
    .flatMap((question) => [normalizeOptionalString(question?.freeTextFieldId)])
    .filter(Boolean)
    .map((fieldId) => fieldId.toLowerCase()),
);

function getQuestion(questionnaireType, responseKey) {
  const normalizedQuestionnaireType =
    normalizeOptionalString(questionnaireType).toLowerCase();
  const normalizedResponseKey = normalizeOptionalString(responseKey).toLowerCase();

  if (!normalizedQuestionnaireType || !normalizedResponseKey) {
    return null;
  }

  return QUESTIONNAIRE_QUESTION_MAPS[normalizedQuestionnaireType]?.get(
    normalizedResponseKey,
  ) || null;
}

function formatRadioAnswer(question, rawValue) {
  const option = Array.isArray(question?.options)
    ? question.options.find((entry) => String(entry.value) === String(rawValue))
    : null;

  return option?.label || formatFallbackAnswer(rawValue);
}

function formatCheckboxAnswer(question, rawValue, formData) {
  if (!Array.isArray(rawValue) || rawValue.length === 0) {
    return "N/A";
  }

  const otherText = normalizeOptionalString(
    formData?.[question?.freeTextFieldId || ""],
  );

  return rawValue
    .map((value) => {
      const option = Array.isArray(question?.options)
        ? question.options.find((entry) => String(entry.value) === String(value))
        : null;
      const label = option?.label || formatFallbackAnswer(value);

      if (
        otherText &&
        String(value) === String(question?.freeTextTriggerValue || "other")
      ) {
        return `${label}: ${otherText}`;
      }

      return label;
    })
    .join(", ");
}

function formatEpisodeTimingAnswer(question, rawValue) {
  const timingValue = rawValue && typeof rawValue === "object" ? rawValue : null;

  if (!timingValue) {
    return "N/A";
  }

  if (timingValue.unknown === true) {
    return question?.timingFields?.unknownLabel || "I can't remember these details";
  }

  const onsetDate = timingValue.approximateOnsetDate
    ? new Date(timingValue.approximateOnsetDate)
    : null;
  const ongoingOption = Array.isArray(question?.timingFields?.ongoingOptions)
    ? question.timingFields.ongoingOptions.find(
        (option) => option.value === timingValue.ongoing,
      )
    : null;

  return [
    `${question?.timingFields?.onsetDateLabel || "Approximate start date"}: ${
      onsetDate && !Number.isNaN(onsetDate.getTime())
        ? onsetDate.toLocaleDateString()
        : timingValue.approximateOnsetDate || "N/A"
    }`,
    `${question?.timingFields?.durationDaysLabel || "Duration in days"}: ${
      timingValue.durationDays ?? "N/A"
    }`,
    `${question?.timingFields?.ongoingLabel || "Is it still ongoing?"}: ${
      ongoingOption?.label ||
      (timingValue.ongoing === true
        ? "Yes"
        : timingValue.ongoing === false
        ? "No"
        : "N/A")
    }`,
  ].join("\n");
}

export function buildQuestionnaireReviewFormData(entry) {
  const responseItems = Array.isArray(entry?.responseItems) ? entry.responseItems : [];

  if (responseItems.length > 0) {
    return responseItems.reduce((formData, item) => {
      const key = normalizeOptionalString(item?.key);

      if (key) {
        formData[key] = item?.value;
      }

      return formData;
    }, {});
  }

  if (entry?.responses && typeof entry.responses === "object") {
    return entry.responses;
  }

  if (entry?.payload?.responses && typeof entry.payload.responses === "object") {
    return entry.payload.responses;
  }

  return {};
}

export function formatQuestionnaireReviewLabel(questionnaireType, responseKey) {
  return (
    getQuestion(questionnaireType, responseKey)?.title ||
    humanizeToken(responseKey) ||
    "Question"
  );
}

export function isQuestionnaireReviewAuxiliaryField(responseKey) {
  const normalizedResponseKey = normalizeOptionalString(responseKey).toLowerCase();
  return normalizedResponseKey
    ? QUESTIONNAIRE_AUXILIARY_FIELD_IDS.has(normalizedResponseKey)
    : false;
}

export function formatQuestionnaireReviewAnswer(
  questionnaireType,
  responseKey,
  formData,
) {
  const question = getQuestion(questionnaireType, responseKey);
  const rawValue = formData?.[responseKey];

  if (!question) {
    return formatFallbackAnswer(rawValue);
  }

  switch (question.fieldType) {
    case "radio":
    case "select":
      return formatRadioAnswer(question, rawValue);
    case "checkbox-group":
      return formatCheckboxAnswer(question, rawValue, formData);
    case "episode-timing":
      return formatEpisodeTimingAnswer(question, rawValue);
    default:
      return formatFallbackAnswer(rawValue);
  }
}
